import {
  commercialGridDefaultsActiveForViewer,
  getCommercialGridDefaultFilterModel,
  normalizeCommercialGridDefaults,
} from "./commercialGridFilterDefaults";
import {
  isAccountantRoleType,
  isOspEngineeringRoleType,
} from "./projectLandingTabAccess";
import {
  ACCOUNTANT_TYPE_ID,
  OSP_ENGINEERING_TYPE_ID,
  getViewerRoleType,
  getViewerUserTypeId,
  loadUserMatrixSettings,
} from "./userMatrixStorage";

let commercialGridDefaultsCache = normalizeCommercialGridDefaults(null);

export function hydrateCommercialGridDefaultsFromServer(raw) {
  commercialGridDefaultsCache = normalizeCommercialGridDefaults(raw);
}

export function clearCommercialGridDefaultsHydration() {
  commercialGridDefaultsCache = normalizeCommercialGridDefaults(null);
}

export function loadCommercialGridDefaults() {
  return commercialGridDefaultsCache;
}

function normalizeRoleName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** True when two matrix role types should share the same commercial default-filter rules. */
function rolesEquivalentForGridDefaults(viewerRole, entryRole) {
  if (!viewerRole || !entryRole) return false;
  if (String(viewerRole.id || "") === String(entryRole.id || "")) return true;
  if (isOspEngineeringRoleType(viewerRole) && isOspEngineeringRoleType(entryRole)) return true;
  if (isAccountantRoleType(viewerRole) && isAccountantRoleType(entryRole)) return true;
  const a = normalizeRoleName(viewerRole.name);
  const b = normalizeRoleName(entryRole.name);
  return Boolean(a && a === b);
}

/**
 * Viewer userTypeId plus persona-equivalent / seed ids so role defaults still apply
 * when the matrix has duplicate OSP/Accountant types or orphaned seed ids in settings.
 */
export function getMatchingUserTypeIdsForCommercialDefaults(viewerEmail) {
  const ids = new Set();
  const viewerTypeId = getViewerUserTypeId(viewerEmail);
  if (viewerTypeId) ids.add(String(viewerTypeId));
  const viewerRole = getViewerRoleType(viewerEmail);
  if (!viewerRole) return ids;

  const userTypes = loadUserMatrixSettings()?.userTypes || [];
  for (const t of userTypes) {
    if (rolesEquivalentForGridDefaults(viewerRole, t)) {
      ids.add(String(t.id));
    }
  }
  // Orphaned config may still reference seed ids after a role was recreated.
  if (isOspEngineeringRoleType(viewerRole)) ids.add(OSP_ENGINEERING_TYPE_ID);
  if (isAccountantRoleType(viewerRole)) ids.add(ACCOUNTANT_TYPE_ID);
  return ids;
}

export function getCommercialGridDefaultFilterModelForGrid() {
  const matchingUserTypeIds = getMatchingUserTypeIdsForCommercialDefaults();
  const fromConfig = getCommercialGridDefaultFilterModel(
    commercialGridDefaultsCache,
    getViewerUserTypeId(),
    { matchingUserTypeIds }
  );
  if (fromConfig) return fromConfig;
  const role = getViewerRoleType();
  if (isAccountantRoleType(role)) {
    return {
      phase: { filterType: "text", type: "equals", filter: "Accounting" },
    };
  }
  return null;
}

export function commercialGridDefaultsEnabled() {
  return commercialGridDefaultsActiveForViewer(
    commercialGridDefaultsCache,
    getViewerUserTypeId(),
    { matchingUserTypeIds: getMatchingUserTypeIdsForCommercialDefaults() }
  );
}
